// The player's record on this device: each call the account stream closed, newest first, with
// the form guide and streak it makes. Kept per owner in this browser only (the venue keeps the
// lifetime figures); every amount stays the venue's raw integer.

import { big, ratioBp } from "../lib/num";
import type { Closed } from "../sdk";
import { createStore, useStore } from "./store";

export type Play = {
  id: string;
  at: number;
  instrumentId: number;
  symbol: string;
  isLong: boolean;
  leverage: number;
  /** The stake, raw at 18 decimals. */
  margin: string;
  /** What the close paid beyond the stake, raw and signed. */
  net: string;
  busted: boolean;
  paper: string;
};

export type Result = "win" | "loss" | "bust";

export type PlayerRecord = { owner: string | null; plays: Play[] };

const KEPT = 60;
const key = (owner: string) => `playertrade:record:v1:${owner}`;

export const record = createStore<PlayerRecord>({ owner: null, plays: [] });

export const usePlays = () => useStore(record, (state) => state.plays);

function load(owner: string): Play[] {
  try {
    const text = localStorage.getItem(key(owner));
    const parsed = text ? (JSON.parse(text) as Play[]) : [];
    return Array.isArray(parsed) ? parsed.slice(0, KEPT) : [];
  } catch {
    return [];
  }
}

function save(owner: string, plays: Play[]) {
  try {
    localStorage.setItem(key(owner), JSON.stringify(plays));
  } catch {
    // storage blocked: kept for this tab
  }
}

/** Switches the record to `owner` (null clears it). */
export function loadRecord(owner: string | null) {
  if (record.get().owner === owner) return;
  record.set({ owner, plays: owner ? load(owner) : [] });
}

/** Adds what a patch closed; returns the plays it had not seen. */
export function addPlays(owner: string, closed: Closed[], symbolOf: (id: number) => string): Play[] {
  const state = record.get();
  const plays = state.owner === owner ? state.plays : load(owner);
  const seen = new Set(plays.map((play) => play.id));
  const now = Date.now();
  const fresh: Play[] = closed
    .filter((item) => !seen.has(item.positionId))
    .map((item) => ({
      id: item.positionId,
      at: now,
      instrumentId: item.instrumentId,
      symbol: symbolOf(item.instrumentId),
      isLong: item.isLong,
      leverage: item.leverage,
      margin: item.margin,
      net: item.netPnl,
      busted: item.liquidated,
      paper: item.paperMinted,
    }));
  if (fresh.length === 0) return [];
  const next = [...fresh, ...plays].slice(0, KEPT);
  record.set({ owner, plays: next });
  save(owner, next);
  return fresh;
}

export const resultOf = (play: Play): Result => (play.busted ? "bust" : big(play.net) > 0n ? "win" : "loss");

/** What the close returned per dollar staked, in hundredths of a percent (10000 is 1×). */
export const multipleBp = (play: Pick<Play, "margin" | "net">): bigint => {
  const margin = big(play.margin);
  const back = margin + big(play.net);
  return back <= 0n ? 0n : ratioBp(back, margin);
};

/** Wins in a row (positive) or calls without a win in a row (negative), newest first. */
export function streakOf(plays: Play[]): number {
  const first = plays[0];
  if (!first) return 0;
  const won = resultOf(first) === "win";
  let count = 0;
  for (const play of plays) {
    if ((resultOf(play) === "win") !== won) break;
    count += 1;
  }
  return won ? count : -count;
}

export type Tally = { wins: number; losses: number; busts: number; net: bigint; best: bigint };

export function tally(plays: Play[]): Tally {
  let wins = 0;
  let losses = 0;
  let busts = 0;
  let net = 0n;
  let best = 0n;
  for (const play of plays) {
    const result = resultOf(play);
    if (result === "win") wins += 1;
    else if (result === "bust") busts += 1;
    else losses += 1;
    net += big(play.net);
    const multiple = multipleBp(play);
    if (multiple > best) best = multiple;
  }
  return { wins, losses, busts, net, best };
}
